import axios from 'axios';

const API_BASE_URL = process.env.VUE_APP_API_URL;

// Configuração do axios
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Interceptor para adicionar token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Serviço para gerenciar classificação do campeonato
 */
class StandingsService {

  /**
   * Busca a classificação atual do campeonato
   * @returns {Promise<Object>} Classificação completa
   */
  static async getCurrentStandings() {
    try {
      const response = await apiClient.get('/standings/brasileirao_2026', {
        headers: { permission: ['betting'] }
      });
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar classificação atual:', error);
      throw error;
    }
  }

  /**
   * Busca classificação de uma rodada específica
   * @param {number} roundNumber - Número da rodada
   * @returns {Promise<Object>} Classificação da rodada
   */
  static async getStandingsByRound(roundNumber) {
    try {
      const response = await apiClient.get(`/standings/round/${roundNumber}`, {
        headers: { permission: ['betting'] }
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar classificação da rodada ${roundNumber}:`, error);
      throw error;
    }
  }

  /**
   * Busca classificação por temporada
   * @param {number} season - Ano da temporada (ex: 2025)
   * @returns {Promise<Object>} Classificação da temporada
   */
  static async getStandingsBySeason(season) {
    try {
      const response = await apiClient.get(`/standings/season/${season}`, {
        headers: { permission: ['betting'] }
      });
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar classificação da temporada ${season}:`, error);
      throw error;
    }
  }

  /**
   * Formata a sequência de resultados (form) com emojis
   * @param {string} form - String de resultados (ex: "WWDWL")
   * @returns {string} Resultados formatados com emojis
   */
  static formatForm(form) {
    if (!form) return '';
    
    return form.split('').map(result => {
      switch(result) {
        case 'W': return '🟢'; // Vitória
        case 'D': return '🟡'; // Empate
        case 'L': return '🔴'; // Derrota
        default: return result;
      }
    }).join(' ');
  }

  /**
   * Retorna a classe CSS baseada na descrição da posição
   * @param {string} description - Descrição da posição (ex: "Libertadores", "Rebaixamento")
   * @returns {string} Classe CSS
   */
  static getPositionClass(description) {
    if (!description) return '';
    
    const desc = description.toLowerCase();
    
    if (desc.includes('libertadores')) {
      return 'bg-blue-100 border-l-4 border-blue-500';
    } else if (desc.includes('sul-americana') || desc.includes('sulamericana')) {
      return 'bg-orange-100 border-l-4 border-orange-500';
    } else if (desc.includes('rebaixamento')) {
      return 'bg-red-100 border-l-4 border-red-500';
    }
    
    return '';
  }

  /**
   * Retorna ícone baseado no status da posição
   * @param {string} status - Status (up, down, same)
   * @returns {string} Ícone
   */
  static getStatusIcon(status) {
    switch(status) {
      case 'up': return '⬆️';
      case 'down': return '⬇️';
      case 'same': return '➖';
      default: return '';
    }
  }

  /**
   * Calcula o aproveitamento do time
   * @param {number} points - Pontos conquistados
   * @param {number} played - Jogos disputados
   * @returns {string} Aproveitamento em porcentagem
   */
  static calculatePerformance(points, played) {
    if (!played) return '0%';
    const percentage = (points / (played * 3)) * 100;
    return percentage.toFixed(1) + '%';
  }
}

export default StandingsService;
